import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, CalendarPlus } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Chips, Loading, Empty, Modal, Uploader } from "@/components/atoms";

const CATS = ["All", "Traditional", "Blackwork", "Color", "Realism", "Neo-Traditional", "Fine Line"];

export default function Gallery({ section = "gallery", allowBook = false, title = "Gallery", eyebrow = "Healed & Fresh" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [cat, setCat] = useState("All");
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ image_url: "", caption: "", category: "Traditional" });

  const load = async (c) => {
    setItems(null);
    try {
      const params = { section };
      if (c && c !== "All") params.category = c;
      const { data } = await api.get("/gallery", { params });
      setItems(data);
    } catch (e) { setItems([]); }
  };
  useEffect(() => {
    load(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, section]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/gallery", { ...form, section });
      toast.success("Added");
      setAddOpen(false);
      setForm({ image_url: "", caption: "", category: "Traditional" });
      load(cat);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/gallery/${id}`); setActive(null); load(cat); toast.success("Deleted"); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid={`${section}-page`}>
      <div className="px-5 pt-5 flex items-center justify-between">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h1 className="font-display font-black text-3xl uppercase leading-tight">{title}</h1>
        </div>
        {isAdmin && (
          <button className="atc-btn btn-gold px-3 h-11" onClick={() => setAddOpen(true)} data-testid={`${section}-add-btn`}>
            <Plus size={18} /> Add
          </button>
        )}
      </div>
      <Chips options={CATS} value={cat} onChange={setCat} testid={`${section}-chip`} />

      {items === null ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty title={section === "flash" ? "No flash designs yet" : "No photos here yet"} />
      ) : (
        <div className="px-4 pb-8" style={{ columnCount: 2, columnGap: "12px" }}>
          {items.map((it) => (
            <button
              key={it.id}
              className="mb-3 w-full block break-inside-avoid border-2 border-black rounded overflow-hidden rise-in"
              onClick={() => setActive(it)}
              data-testid={`${section}-item-${it.id}`}
            >
              <img src={fileUrl(it.image_url)} alt={it.caption} className="w-full object-cover" />
              <div className="p-2 bg-[#EAE4D3] text-left">
                <p className="font-mono2 text-[9px] uppercase text-[#8B1E28]">{it.category}</p>
                <p className="text-xs font-body leading-tight">{it.caption}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      <Modal open={!!active} onClose={() => setActive(null)} title={active?.category || ""} testid={`${section}-modal`}>
        {active && (
          <div>
            <img src={fileUrl(active.image_url)} alt={active.caption} className="w-full object-cover border-2 border-black rounded mb-3" />
            <p className="font-body">{active.caption}</p>
            {allowBook && (
              <button
                className="atc-btn btn-primary w-full mt-4"
                onClick={() => navigate("/bookings", { state: { prefill: { tattoo_idea: `Flash design: ${active.caption}`, style: active.category, reference_url: active.image_url } } })}
                data-testid="gallery-book-btn"
              >
                <CalendarPlus size={16} /> Book This Design
              </button>
            )}
            {isAdmin && (
              <button className="atc-btn btn-outline w-full mt-2" onClick={() => del(active.id)} data-testid="gallery-delete-btn">
                <Trash2 size={16} /> Delete
              </button>
            )}
          </div>
        )}
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={section === "flash" ? "Add Flash Design" : "Add Photo"} testid={`${section}-add-modal`}>
        <form onSubmit={add} className="flex flex-col gap-3">
          {form.image_url && <img src={fileUrl(form.image_url)} alt="preview" className="w-full h-40 object-cover border-2 border-black rounded" />}
          <input className="atc-input" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid={`${section}-url`} />
          <Uploader onUploaded={(url) => setForm({ ...form, image_url: url })} label="Or upload image" testid={`${section}-upload`} />
          <input className="atc-input" placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} required data-testid={`${section}-caption`} />
          <select className="atc-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid={`${section}-category`}>
            {CATS.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
          </select>
          <button type="submit" className="atc-btn btn-primary" data-testid={`${section}-save-btn`}>Add</button>
        </form>
      </Modal>
    </div>
  );
}
